import { useMemo } from "react";
import type { User } from "../services/api";
import { isRealUserEmail } from "../utils/userContactUtils";
import { phoneMatchesSearch } from "../utils/phoneUtils";
import { matchGuardiansByChildQuery } from "./useFamilySearchIndex";
import type { FamilySearchIndexEntry } from "../services/api/family";
import type { AliasFilter, UserContactFilter } from "./useUserListFilterState";

export type EnrichedUser = User & {
  alias?: string;
  matchedFamilyLabel?: string;
};

interface UseFilteredUsersArgs {
  users: User[];
  searchQuery: string;
  activeFilter: UserContactFilter;
  isNoSubcirclesFilter: boolean;
  isAliasDataLoading: boolean;
  allSubcircleMemberIds: Set<string>;
  subcircleMemberIds: Set<string> | null;
  aliasByUserId: Record<string, string>;
  aliasFilter: AliasFilter;
  familySearchIndex: FamilySearchIndexEntry[];
  familySearchEnabled: boolean;
}

export function useFilteredUsers({
  users,
  searchQuery,
  activeFilter,
  isNoSubcirclesFilter,
  isAliasDataLoading,
  allSubcircleMemberIds,
  subcircleMemberIds,
  aliasByUserId,
  aliasFilter,
  familySearchIndex,
  familySearchEnabled,
}: UseFilteredUsersArgs): EnrichedUser[] {
  return useMemo(() => {
    let filtered = users;

    if (activeFilter === "mobile") {
      filtered = filtered.filter(
        (user) => user.phoneNumber && user.phoneNumber.trim() !== ""
      );
    } else if (activeFilter === "email") {
      filtered = filtered.filter((user) => isRealUserEmail(user.email));
    }

    if (isNoSubcirclesFilter) {
      if (isAliasDataLoading) {
        filtered = [];
      } else {
        filtered = filtered.filter(
          (user) => !allSubcircleMemberIds.has(user.id)
        );
      }
    } else if (subcircleMemberIds) {
      filtered = filtered.filter((user) => subcircleMemberIds.has(user.id));
    }

    if (aliasFilter === "with") {
      filtered = filtered.filter((user) => Boolean(aliasByUserId[user.id]));
    } else if (aliasFilter === "without") {
      filtered = filtered.filter((user) => !aliasByUserId[user.id]);
    }

    const childMatches =
      familySearchEnabled && searchQuery.trim()
        ? matchGuardiansByChildQuery(familySearchIndex, searchQuery)
        : new Map<string, string[]>();

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((user) => {
        const alias = aliasByUserId[user.id];
        return (
          (isRealUserEmail(user.email) &&
            user.email.toLowerCase().includes(q)) ||
          user.name.toLowerCase().includes(q) ||
          phoneMatchesSearch(user.phoneNumber, searchQuery) ||
          (alias && alias.toLowerCase().includes(q)) ||
          childMatches.has(user.id)
        );
      });
    }

    return filtered.map((user) => {
      const alias = aliasByUserId[user.id];
      const matchedChildren = childMatches.get(user.id);
      const matchedFamilyLabel =
        matchedChildren && matchedChildren.length > 0
          ? `Family: ${matchedChildren.slice(0, 2).join(", ")}${
              matchedChildren.length > 2
                ? ` +${matchedChildren.length - 2}`
                : ""
            }`
          : undefined;
      return {
        ...user,
        ...(alias ? { alias } : {}),
        ...(matchedFamilyLabel ? { matchedFamilyLabel } : {}),
      };
    });
  }, [
    users,
    searchQuery,
    activeFilter,
    isNoSubcirclesFilter,
    isAliasDataLoading,
    allSubcircleMemberIds,
    subcircleMemberIds,
    aliasByUserId,
    aliasFilter,
    familySearchIndex,
    familySearchEnabled,
  ]);
}
